import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import { fn, get, hash } from "@ember/helper";
import EmberObject, { action, computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { LinkTo } from "@ember/routing";
import { service } from "@ember/service";
import { camelize } from "@ember/string";
import { trustHTML } from "@ember/template";
import BufferedProxy from "ember-buffered-proxy/proxy";
import DButton from "discourse/components/d-button";
import { popupAjaxError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import { INPUT_DELAY, isProduction } from "discourse/lib/environment";
import { or } from "discourse/truth-helpers";
import { i18n } from "discourse-i18n";
import Circle from "../../models/circle";
import FormFieldComboBox from "./form-fields/combo-box";
import FormFieldCustom from "./form-fields/custom";
import FormFieldDropdown from "./form-fields/dropdown";
import FormFieldEditor from "./form-fields/editor";
import FormFieldImageUploader from "./form-fields/image-uploader";
import FormFieldText from "./form-fields/text";

export default class FormProfileFields extends Component {
  @service dialog;
  @service siteSettings;

  @tracked submitted = false;
  @tracked isSaving = false;
  @tracked buffer;
  @tracked nameValidation = null;

  // TODO (saquetim) replace this magic numbers with a proper configuration setting
  #RECOMMENDED_LOGO_IMAGE_SIZE = {
    width: 400,
    height: 400,
  };

  // TODO (saquetim) replace this magic numbers with a proper configuration setting
  #RECOMMENDED_BACKGROUND_IMAGE_SIZE = {
    width: 1920,
    height: 500,
  };

  constructor() {
    super(...arguments);

    this.buffer = BufferedProxy.create({
      content: this.args.model,
    });
  }

  get disableRichEditor() {
    return this.siteSettings.discourse_circles_use_simple_input_for_description;
  }

  @computed("submitted", "buffer.definition_raw")
  get definitionRawValidation() {
    return this.#fieldValidation("definition_raw");
  }

  @cached
  get customFields() {
    return Circle.customFieldAttributes().map((field) => {
      return {
        id: field.options.id,
        name: field.name,
        displayName: field.displayName,
        description: field.options.description,
        type: field.options.fieldType,
        required: field.isRequired,
        options: field.options.options, // for select fields
      };
    });
  }

  @cached
  get privacyLevelOptions() {
    return Object.entries(Circle.PRIVACY_LEVELS).map(([key, props]) => {
      return {
        icon: props.icon,
        name: i18n(`discourse_circles.form.privacy_levels.${key}.name`),
        label: i18n(`discourse_circles.form.privacy_levels.${key}.name`),
        help: i18n(`discourse_circles.form.privacy_levels.${key}.help`),
        description: i18n(`discourse_circles.form.privacy_levels.${key}.help`),
        value: props.value,
      };
    });
  }

  get privacyDescription() {
    return this.privacyLevelOptions[this.buffer.get("privacy_level")]?.help;
  }

  @computed("submitted", "buffer.privacy_level")
  get privacyLevelValidation() {
    return this.#fieldValidation("privacy_level");
  }

  @computed(
    "submitted",
    ...Circle.customFieldAttributes().map((f) => `buffer.${f.name}`)
  )
  get customFieldsValidation() {
    const validations = EmberObject.create();

    Circle.customFieldAttributes().forEach((field) => {
      validations.set(field.name, this.#fieldValidation(field.name));
    });

    return validations;
  }

  @cached
  get mentionableLevelOptions() {
    return [
      { name: i18n("groups.alias_levels.nobody"), value: 0 },
      { name: i18n("groups.alias_levels.only_admins"), value: 1 },
      { name: i18n("groups.alias_levels.mods_and_admins"), value: 2 },
      { name: i18n("groups.alias_levels.members_mods_and_admins"), value: 3 },
      { name: i18n("groups.alias_levels.owners_mods_and_admins"), value: 4 },
      { name: i18n("groups.alias_levels.everyone"), value: 99 },
    ];
  }

  get logoImageRecommendations() {
    return i18n(
      "discourse_circles.form.logo.recommendations",
      this.#RECOMMENDED_LOGO_IMAGE_SIZE
    );
  }

  get logoImageUrl() {
    return this.buffer.get("uploaded_logo")?.url || "";
  }

  get logoDarkImageUrl() {
    return this.buffer.get("uploaded_logo_dark")?.url || "";
  }

  get backgroundImageRecommendations() {
    return i18n(
      "discourse_circles.form.background.recommendations",
      this.#RECOMMENDED_BACKGROUND_IMAGE_SIZE
    );
  }

  get backgroundImageUrl() {
    return this.buffer.get("uploaded_background")?.url || "";
  }

  get backgroundDarkImageUrl() {
    return this.buffer.get("uploaded_background_dark")?.url || "";
  }

  get saveBtnText() {
    return this.isSaving ? "saving" : this.args.saveBtnText || "save";
  }

  @computed("buffer.hasChanges")
  get disableSave() {
    return !this.buffer.hasChanges;
  }

  @dependentKeyCompat
  get inputContainsErrors() {
    Circle.attributeNames().forEach((name) => this.buffer.get(name));

    const fieldNames = Circle.attributes()
      .filter((field) => field.hasValidations && !field.options.isCustomField)
      .map((field) => field.name);

    if (this.customFields.length) {
      // custom fields are validated all at once so we include a pseudo field name to force
      // checking them here
      fieldNames.push("customFields");
    }

    for (const fieldName of fieldNames) {
      const validationFieldName = `${camelize(fieldName)}Validation`;

      // dev only check to verify if all validation fields were set in the class prototype
      if (!isProduction() && !(validationFieldName in this)) {
        // eslint-disable-next-line no-console
        console.warn(
          `MISSING VALIDATION: ${validationFieldName}: validation field for ${fieldName} is not set in the class prototype`
        );
      }

      if (this[validationFieldName]?.failed) {
        return true;
      }
    }

    return false;
  }

  #fieldValidation(name) {
    if (!this.submitted && !this.buffer.hasChanged(name)) {
      return null;
    }

    const validation = Circle.attributeByName(name).validate(
      this.buffer.get(name)
    );

    return validation.errors?.length
      ? {
          failed: true,
          reason: validation.errors.join("; "),
        }
      : null;
  }

  @cached
  get maxNameLength() {
    return Circle.attributeByName("name").maxLength;
  }

  @cached
  get maxDescriptionLength() {
    return Circle.attributeByName("definition_raw").maxLength;
  }

  @action
  logoUploadDone(upload) {
    this.buffer.set("uploaded_logo", upload);
  }

  @action
  logoUploadDeleted() {
    this.buffer.set("uploaded_logo", {});
  }

  @action
  logoDarkUploadDone(upload) {
    this.buffer.set("uploaded_logo_dark", upload);
  }

  @action
  logoDarkUploadDeleted() {
    this.buffer.set("uploaded_logo_dark", {});
  }

  @action
  backgroundUploadDone(upload) {
    this.buffer.set("uploaded_background", upload);
  }

  @action
  backgroundUploadDeleted() {
    this.buffer.set("uploaded_background", {});
  }

  @action
  backgroundDarkUploadDone(upload) {
    this.buffer.set("uploaded_background_dark", upload);
  }

  @action
  backgroundDarkUploadDeleted() {
    this.buffer.set("uploaded_background_dark", {});
  }

  @action
  async save() {
    this.submitted = true;

    if (!(await this.#validateFields())) {
      this.dialog.alert(i18n("discourse_circles.form.fields_with_errors"));
      return;
    }

    this.isSaving = true;

    try {
      const model = this.args.model;

      this.args.onBeforeSave?.(model);

      const attrs = this.buffer.getProperties(Circle.attributeNames());
      await model.save(attrs);
      this.buffer.applyChanges();

      // optional afterSave trigger
      this.args.onAfterSave?.(model);
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.submitted = false;
      this.isSaving = false;
    }
  }

  async #validateFields() {
    let success = true;

    for (const fieldName of Circle.attributeNames()) {
      const validation = await Promise.resolve(
        Circle.attributeByName(fieldName).validate(
          this.buffer.get(fieldName),
          this.buffer,
          {
            model: this.args.model,
          }
        )
      );

      if (
        validation.errors?.length // or if there are no errors
      ) {
        success = false;
      }
    }

    return success;
  }

  @bind
  validateName() {
    const updateStatus = (status, message) => {
      switch (status) {
        case "ok":
          this.nameValidation = { reason: message };
          break;
        case "loading":
          this.nameValidation = { loading: true, reason: message };
          break;
        default:
          this.nameValidation = {
            failed: true,
            reason: message,
          };
          break;
      }
    };

    discourseDebounce(
      `circle_${this.args.model.category_id}::validateName`,
      async () => {
        const { success, errors } = await Circle.attributeByName(
          "name"
        ).validate(this.buffer.get("name"), this.buffer, {
          updateStatus,
          model: this.args.model,
        });

        if (success) {
          this.nameValidation =
            success === true
              ? null
              : {
                  failed: false,
                  reason: success,
                };

          return;
        }

        this.nameValidation = errors
          ? {
              failed: true,
              reason: errors.join("; "),
            }
          : null;
      },
      INPUT_DELAY,
      true
    );
  }

  <template>
    <form class="circles-form form-vertical">
      <FormFieldText
        @name="name"
        @required={{true}}
        @autofocus="autofocus"
        @value={{this.buffer.name}}
        @onChange={{this.validateName}}
        @maxLength={{this.maxNameLength}}
        @validation={{this.nameValidation}}
      />

      <FormFieldEditor
        @name="definition_raw"
        @disableRichEditor={{this.disableRichEditor}}
        @required={{this.siteSettings.discourse_circles_require_description}}
        @value={{this.buffer.definition_raw}}
        @maxLength={{this.maxDescriptionLength}}
        @validation={{this.definitionRawValidation}}
      />

      <FormFieldDropdown
        @name="privacy"
        @showDescription={{true}}
        @required={{true}}
        @valueProperty="value"
        @value={{this.buffer.privacy_level}}
        @noneI18n="discourse_circles.form.privacy.placeholder"
        @content={{this.privacyLevelOptions}}
        @onChange={{fn (mut this.buffer.privacy_level)}}
        @options={{hash
          castInteger=true
          autoInsertNoneItem=false
          showCaret=true
        }}
      >
        <div class="control-help">
          {{trustHTML this.privacyDescription}}
        </div>
      </FormFieldDropdown>

      <FormFieldComboBox
        @name="mentionable_level"
        @showDescription={{true}}
        @required={{true}}
        @valueProperty="value"
        @value={{this.buffer.mentionable_level}}
        @content={{this.mentionableLevelOptions}}
        @onChange={{fn (mut this.buffer.mentionable_level)}}
      />

      {{#each this.customFields as |field|}}
        <FormFieldCustom
          @field={{field}}
          @buffer={{this.buffer}}
          @validation={{get this.customFieldsValidation field.name}}
        />
      {{/each}}

      <FormFieldImageUploader
        class="no-repeat contain-image"
        @name="logo"
        @imageUrl={{this.logoImageUrl}}
        @onUploadDone={{this.logoUploadDone}}
        @onUploadDeleted={{this.logoUploadDeleted}}
      >
        <div class="control-help">
          {{this.logoImageRecommendations}}
        </div>
      </FormFieldImageUploader>

      <FormFieldImageUploader
        class="no-repeat contain-image"
        @name="logo_dark"
        @imageUrl={{this.logoDarkImageUrl}}
        @onUploadDone={{this.logoDarkUploadDone}}
        @onUploadDeleted={{this.logoDarkUploadDeleted}}
      >
        <div class="control-help">
          {{this.logoImageRecommendations}}
        </div>
      </FormFieldImageUploader>

      <FormFieldImageUploader
        class="no-repeat contain-image"
        @name="background"
        @imageUrl={{this.backgroundImageUrl}}
        @onUploadDone={{this.backgroundUploadDone}}
        @onUploadDeleted={{this.backgroundUploadDeleted}}
      >
        <div class="control-help">
          {{this.backgroundImageRecommendations}}
        </div>
      </FormFieldImageUploader>

      <FormFieldImageUploader
        class="no-repeat contain-image"
        @name="background_dark"
        @imageUrl={{this.backgroundDarkImageUrl}}
        @onUploadDone={{this.backgroundDarkUploadDone}}
        @onUploadDeleted={{this.backgroundDarkUploadDeleted}}
      >
        <div class="control-help">
          {{this.backgroundImageRecommendations}}
        </div>
      </FormFieldImageUploader>

      <div class="controls buttons">
        <DButton
          @action={{this.save}}
          @type="submit"
          @disabled={{or this.isSaving this.disableSave}}
          @label={{this.saveBtnText}}
          class="btn btn-primary circles-save"
        />
        {{#unless @hideCancelBtn}}
          <LinkTo @route="circles.index">
            {{i18n "cancel"}}
          </LinkTo>
        {{/unless}}
      </div>
    </form>
  </template>
}
